import { DragContext } from '@/ui/DragView'
import React from 'react'
import { InteractAscendancyButton, loadTree, renderTree } from './renderItems'
import { Renderer } from './renderer'

import { ItemTooltip, PassiveSkillTooltip } from '@/tooltip'
import { ManualTooltip, TooltipAction } from '@/ui/tooltip'

import { TreeViewState } from '@/logic/tree'
import Store from '@/store'
import Popup from '@/ui/popup'
import { TreeDataProvider } from '../logic/treeData'
import { CanvasRenderer } from './canvasRenderer'
import { ErrorRenderer } from './errorRenderer'
import { WebGLRenderer } from './glRenderer'
import './viewer.scss'

function VirtualNode({
  data,
  id,
  onClose,
  touch,
  nodeActions
}: {
  data: TreeDataProvider
  id: number
  onClose: () => void
  touch: boolean
  nodeActions?: (id: number) => TooltipAction[] | undefined
}) {
  const node = data.node(id)
  const { transform } = React.useContext(DragContext)

  const actions = React.useMemo(() => nodeActions?.(id), [id, nodeActions])

  const ref = React.useRef<HTMLDivElement>(null)

  if (!node) return

  const style = {
    left: node.x * transform.scale + transform.x,
    top: node.y * transform.scale + transform.y,
    fontSize: 256 * transform.scale
  }

  const activeMasteries = node?.skill.mastery && data.activeMasteries(node.skill.mastery.count_stat)

  return (
    <React.Fragment>
      <ManualTooltip
        nodeRef={ref}
        touch={touch}
        onClose={onClose}
        actions={actions}
        lockKey="alt"
        tooltip={
          <div className="poe-CompositeTooltip">
            {node.jewel && <ItemTooltip item={node.jewel} />}
            <PassiveSkillTooltip
              id={id}
              skill={data.nodeSkill(id)!}
              masteryIndex={node.masteryIndex}
              atlas={data.atlas}
              activeMasteries={activeMasteries}
            />
          </div>
        }
      />
      <div className="poe-virtual-node" style={style} ref={ref} />
    </React.Fragment>
  )
}

function useDelay(duration: number) {
  const [done, setDone] = React.useState(false)
  const durationRef = React.useRef(duration)
  React.useEffect(() => {
    setTimeout(() => {
      if (durationRef.current >= 0) setDone(true)
    }, durationRef.current)
    return () => {
      durationRef.current = -1
    }
  }, [])
  return done
}

function MasteryPopup({
  canvasRef,
  data,
  id,
  onSelect
}: {
  canvasRef: React.RefObject<HTMLCanvasElement>
  data: TreeDataProvider
  id: number
  onSelect: (id: number, index: number | undefined) => void
}) {
  const node = data.node(id)
  const { transform } = React.useContext(DragContext)

  const preventBubbling = React.useCallback((e: React.PointerEvent) => e.stopPropagation(), [])

  if (!useDelay(10)) return // Delay the popup a little otherwise it will close instantly
  if (!node || !canvasRef.current) return

  const canvasRect = canvasRef.current.getBoundingClientRect()
  const x = canvasRect.left + node.x * transform.scale + transform.x
  const y = canvasRect.top + node.y * transform.scale + transform.y
  const alignRect = {
    left: x - 128 * transform.scale,
    top: y - 128 * transform.scale,
    right: x + 128 * transform.scale,
    bottom: y + 128 * transform.scale
  }

  const activeMasteries = node?.skill.mastery && data.activeMasteries(node.skill.mastery.count_stat)

  return (
    <Popup alignPosition={alignRect} onClose={() => onSelect(id, undefined)} alignPadding={0}>
      <div onPointerMove={preventBubbling} onPointerDown={preventBubbling} onPointerUp={preventBubbling}>
        <PassiveSkillTooltip
          skill={node.skill}
          atlas={data.atlas}
          onSelect={(index) => onSelect(id, index)}
          activeMasteries={activeMasteries}
        />
      </div>
    </Popup>
  )
}

export function TreeRenderer({
  state,
  canvasFallback,
  onHover,
  onClick,
  onSelectMastery,
  nodeActions
}: {
  state: TreeViewState
  canvasFallback?: boolean
  onHover?: (id: number | undefined) => void
  onClick?: (id: number, e: React.MouseEvent) => void
  onSelectMastery?: (id: number, index: number | undefined) => void
  nodeActions?: (id: number) => TooltipAction[] | undefined
}) {
  const [touch, setTouch] = React.useState(false)
  const dragState = React.useContext(DragContext)
  const ref = React.useRef<HTMLCanvasElement>(null)
  const [contextLost, setContextLost] = React.useState(false)
  const [ascendancyButton, setAscendancyButton] = React.useState<'hover' | 'pressed'>()
  const items = Store.useSelector((store) => store.items)

  React.useEffect(() => {
    if (!ref.current) return
    ref.current.addEventListener('webglcontextlost', () => setContextLost(true))
    ref.current.addEventListener('webglcontextrestored', () => setContextLost(false))
  }, [])

  const [renderer, setRenderer] = React.useState<Renderer>()

  React.useEffect(() => {
    if (contextLost || !ref.current) {
      setRenderer(undefined)
    } else {
      try {
        setRenderer(new WebGLRenderer(ref.current))
      } catch (e) {
        setRenderer(new CanvasRenderer(ref.current, canvasFallback ?? false))
      }
    }
  }, [contextLost, canvasFallback])

  const [display, setDisplay] = React.useState<TreeDataProvider>()
  React.useEffect(() => {
    if (!renderer) return
    //TODO: throttle this promise to fix iOS bug?
    const loader = renderer instanceof ErrorRenderer ? undefined : loadTree(state, renderer)
    if (loader) {
      setDisplay(undefined)
      let current = true
      loader.then(() => {
        if (current) setDisplay(new TreeDataProvider(state, items))
      })
      return () => {
        current = false
      }
    } else {
      setDisplay(new TreeDataProvider(state, items))
    }
  }, [renderer, state, items])

  React.useEffect(() => {
    if (renderer && display) {
      const items = renderTree(display, ascendancyButton)
      renderer.setItems(items)
      renderer.render(state.ascendancyOnly)
    }
  }, [renderer, display, ascendancyButton, state.ascendancyOnly])

  React.useEffect(() => {
    if (renderer) {
      renderer.update(dragState)
      renderer.render(state.ascendancyOnly)
    }
  }, [renderer, dragState, state.ascendancyOnly])

  const touchPos = React.useRef<{ x: number; y: number }>()
  const touchTimeout = React.useRef<number>()

  const dragPos = React.useRef(dragState.transform)
  dragPos.current = dragState.transform

  if (touchPos.current && touchTimeout.current) {
    const dx = Math.abs(dragPos.current.x - touchPos.current.x)
    const dy = Math.abs(dragPos.current.y - touchPos.current.y)
    if (dx > 50 || dy > 50) {
      window.clearTimeout(touchTimeout.current)
      touchTimeout.current = undefined
    }
  }

  const onMouseDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (touchTimeout.current != null) {
        window.clearTimeout(touchTimeout.current)
        touchTimeout.current = undefined
      }
      const rect = ref.current!.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const key = renderer?.locate(x, y)
      if (key === InteractAscendancyButton) {
        setAscendancyButton('pressed')
      } else if (e.pointerType !== 'mouse') {
        if (!onClick) {
          onHover?.(key)
          setTouch(true)
        } else {
          touchPos.current = { x: dragPos.current.x, y: dragPos.current.y }
          touchTimeout.current = window.setTimeout(() => {
            onHover?.(key)
            setTouch(true)
            touchTimeout.current = undefined
          }, 1000)
        }
      }
    },
    [onHover, renderer, onClick]
  )

  const onMouseMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const rect = ref.current!.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const key = renderer?.locate(x, y)
      if (key === InteractAscendancyButton) {
        setAscendancyButton(e.buttons & 1 ? 'pressed' : 'hover')
        onHover?.(undefined)
      } else {
        setAscendancyButton(undefined)
        onHover?.(key)
      }
      setTouch(false)
    },
    [onHover, renderer]
  )
  const onMouseLeave = React.useCallback(
    (e?: React.PointerEvent) => {
      if (!e || e.pointerType === 'mouse') {
        onHover?.(undefined)
        setAscendancyButton(undefined)
      }
    },
    [onHover]
  )
  const onMouseUp = React.useCallback(
    (e: React.PointerEvent) => {
      if (!onClick) return
      if (e.pointerType !== 'mouse') {
        if (touchTimeout.current != null) {
          window.clearTimeout(touchTimeout.current)
          touchTimeout.current = undefined
        } else {
          return
        }
      }
      const rect = ref.current!.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const key = renderer?.locate(x, y)
      if (key === InteractAscendancyButton) {
        setAscendancyButton('hover')
      }
      if (e.shiftKey && e.button === 2) return
      if (key) onClick?.(key, e)
    },
    [onClick, renderer]
  )
  const onContextMenu = React.useCallback((e: React.MouseEvent) => {
    if (!e.shiftKey) e.preventDefault()
  }, [])
  React.useEffect(() => {
    return () => {
      if (touchTimeout.current != null) {
        window.clearTimeout(touchTimeout.current)
        touchTimeout.current = undefined
      }
    }
  }, [])

  return (
    <div
      className="poe-outer"
      onPointerDown={onMouseDown}
      onPointerMove={onMouseMove}
      onPointerLeave={onMouseLeave}
      onPointerUp={onMouseUp}
      onContextMenu={onContextMenu}>
      <canvas ref={ref} />
      {display ? (
        <div className="poe-inner">
          {display.state.masteryPopup != null && onSelectMastery ? (
            <MasteryPopup canvasRef={ref} data={display} id={display.state.masteryPopup} onSelect={onSelectMastery} />
          ) : display.state.hover != null ? (
            <VirtualNode
              data={display}
              id={display.state.hover}
              onClose={onMouseLeave}
              touch={touch}
              nodeActions={nodeActions}
            />
          ) : undefined}
        </div>
      ) : (
        <div className="poe-loading">
          <div className="poe-text">Loading...</div>
        </div>
      )}
    </div>
  )
}
